import { PlanCellCoordinatesWithoutPerson } from "../data/plan";
import { MealKey } from "../utils/planUtils";
import { useImmer } from "use-immer";
import {
  DrawerSelection,
  Plan2PlateDrawer,
  Plan2PlateDrawerHeader,
  usePlan2PlateDisclosure,
} from "./Plan2PlateDrawer";
import { DrawerHistoryContext, DrawerHistoryContextType } from "../contexts";
import React, { useContext, useState } from "react";
import { useToast } from "./ui/Toast";
import { SearchForPlanCellDrawer } from "./SearchForPlanCellDrawer";
import { PlanCoordinateHeader } from "./PlanCoordinateHeader";

interface PlanAllDrawerProps {
  coordinates: PlanCellCoordinatesWithoutPerson | undefined;
  hasNonEmptyMeals: boolean;
  hasUnsavedMeals: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConsume: (date: string, mealInd: string, mealKey: MealKey) => void;
  clearHistory: () => Promise<void>;
  cook: (coordinates: PlanCellCoordinatesWithoutPerson) => Promise<void>;
}

export function usePlanAllDrawer(
  onConsume: PlanAllDrawerProps["onConsume"],
  cook: (coordinates: PlanCellCoordinatesWithoutPerson) => Promise<void>,
  drawerHistoryContext: DrawerHistoryContextType,
): PlanAllDrawerProps & {
  open: (
    coordinates: PlanCellCoordinatesWithoutPerson,
    hasNonEmptyMeals: boolean,
    hasUnsavedMeals: boolean,
  ) => Promise<void>;
} {
  const [coordinates, updateCoordinates] = useImmer<
    PlanCellCoordinatesWithoutPerson | undefined
  >(undefined);
  const [hasNonEmptyMeals, setHasNonEmptyMeals] = useState(false);
  const [hasUnsavedMeals, setHasUnsavedMeals] = useState(false);

  const { isOpen, onOpen, onClose } =
    usePlan2PlateDisclosure(drawerHistoryContext);

  async function open(
    coordinates: PlanCellCoordinatesWithoutPerson,
    hasNonEmptyMeals: boolean,
    hasUnsavedMeals: boolean,
  ) {
    updateCoordinates(coordinates);
    setHasNonEmptyMeals(hasNonEmptyMeals);
    setHasUnsavedMeals(hasUnsavedMeals);
    onOpen();
  }

  return {
    onConsume,
    open,
    coordinates,
    hasNonEmptyMeals,
    hasUnsavedMeals,
    isOpen,
    onClose,
    cook,
    clearHistory: drawerHistoryContext.clearHistory,
  };
}

export const PlanAllDrawer = (props: PlanAllDrawerProps) => {
  const toast = useToast();
  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const { isOpen, onOpen, onClose } = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "search-for-plan-cell",
  );
  if (props.coordinates !== undefined) {
    return (
      <>
        {props.coordinates && (
          <SearchForPlanCellDrawer
            onClose={onClose}
            open={isOpen}
            onSubmit={() => {
              onClose();
              props.onClose();
            }}
            planCellCoordinates={props.coordinates}
            planMode={"plan"}
          />
        )}
        <Plan2PlateDrawer
          onClose={props.onClose}
          open={props.isOpen}
          header={
            <PlanCoordinateHeader planCellCoordinates={props.coordinates} />
          }
          body={
            <>
              <DrawerSelection
                dataCy="plan-all-option"
                onClick={onOpen}
                icon="accountArrows"
                heading={"Für alle Planen"}
                description={"Plane für alle Personen"}
              />
              <DrawerSelection
                dataCy="cook-all-option"
                onClick={() => {
                  if (props.coordinates !== undefined) {
                    if (props.hasUnsavedMeals) {
                      toast({
                        title: "Ungespeicherte Gerichte",
                        description:
                          "Es gibt ungespeicherte Gerichte in diesem Zeitraum",
                        status: "info",
                        isClosable: true,
                      });
                    } else if (!props.hasNonEmptyMeals) {
                      toast({
                        title: "Keine Gerichte",
                        description:
                          "Es gibt keine zu kochenden Gerichte für diesen Zeitraum",
                        status: "info",
                        isClosable: true,
                      });
                    } else {
                      props.cook(props.coordinates);
                    }
                  }
                }}
                icon="chickenLeg"
                heading={"Kochen"}
                description={"Öffne den Kochdialog"}
              />
            </>
          }
        />
      </>
    );
  } else {
    return <></>;
  }
};
